/**
 * The NFT Mint area. This is a complete component that takes charge of
 * connecting, contract reading and minting.
 * 
 * USAGE:
 *
 * 1. Install dependencies (assuming you already have a React app where you want to
 * use this): 
 * 
 * npm install --save @metamask/detect-provider web3 classnames
 * 
 * 2. Adjust the component (search for the strings "TODO:" below)
 */

// TODO: Add the contract's ABI json file.
import ABI from './assets/abi-pikzel.json'
import Web3 from 'web3'
import { useEffect, useState, useCallback } from 'react'
import detectEthereumProvider from '@metamask/detect-provider'
import classnames from 'classnames'

// TODO: Add the deployed contract address.
const CONTRACT = '0x39e51016334f5888aed3ea03a05310ff41e18596'

// TODO: Change this to either `first_free` or `free_mint_num`. If `first_free`
// then the the first N NFTs are free, then rest are paid. If `free_mint_num` it
// means the first N NFTs of the collection are designated as free, rest are
// paid.
const FREE_MINT_MODE = 'first_free'

// The link to the Etherscan transaction.
const getTransactionEtherscanLink = hash => {
    if ( window.ethereum.chainId === '0x4' ) {
        return `https://rinkeby.etherscan.io/tx/${ hash }`
    } else {
        return `https://etherscan.io/tx/${ hash }`
    }
}

const NftArea = props => {
    // Connect to the API provided by MetaMask, this will also check if the the
    // current wallet has an existing connection to the site.
    useEffect( () => {
        const init = async () => {
            const provider = await detectEthereumProvider()
            setIsInitialized( provider === window.ethereum )

            // Check if previously connected.
            if ( provider === window.ethereum ) {
                window.ethereum
                    .request({ method: 'eth_accounts' })
                    .then(accounts => {
                        if ( accounts.length ) {
                            setConnecteWallet( accounts[0] )
                        }
                    })
            }
        }
        init()
    }, [] )

    // On-screen messages.
    const [ errorMessage, setErrorMessage ] = useState( '' )
    const [ successMessage, setSuccessMessage ] = useState( '' )
    const [ connectionMessage, setConnectionMessage ] = useState( 'Please wait...' )
    const [ successTransactionUrlMessage, setSuccessTransactionUrlMessage ] = useState( '' )

    // Wallet & connection status states.
    const [ isInitialized, setIsInitialized ] = useState( false )
    const [ connectedWallet, setConnecteWallet ] = useState( '' )
    const [ contract, setContract ] = useState( null )
    
    // Internal states.
    const [ isBusy, setIsBusy ] = useState( false )
    const [ currentPriceToPay, setCurrentPriceToPay ] = useState( '' )

    // Contract & minting data which are read from the contract.
    const [ mintingIsPaused, setMintingIsPaused ] = useState( true )
    const [ totalSupply, setTotalSupply ] = useState( '' )
    const [ maxSupply, setMaxSupply ] = useState( '' )
    const [ mintAmount, setMintAmount ] = useState( 1 )
    const [ maxMintAmount, setMaxMintAmount ] = useState( 1 )
    const [ price, setPrice ] = useState( 0 )
    const [ numFree, setNumFree ] = useState( 0 )
    const [ balance, setBalance ] = useState( 0 )

    // Connect to a wallet. This is called when clicking on the connect button.
    const doConnect = async () => {
        setErrorMessage( '' )
        setSuccessMessage( '' )
        setIsBusy( true )

        // This prompts MetaMask to connect.
        window.ethereum
            .request({ method: 'eth_requestAccounts' })
            .then( accounts => {
                if ( accounts.length ) {
                    setConnecteWallet( accounts[0] )
                }
            } )
            .finally( () => {
                setIsBusy( false )
            } )
    }

    // This grabs all the data in the contract and populates all the necessary states.
    const updateContractStatus = useCallback( ( _contract = contract ) => {
        const update = async () => {
            try {
                // TODO: Grab data from the contract. This will need to be adjusted depending on the contract.
                const totalSupply = parseInt( await _contract.methods.totalSupply().call(), 10 )
                const maxSupply = parseInt( await _contract.methods.MAX_SUPPLY().call(), 10 )
                let maxMintsPerWallet = parseInt( await _contract.methods.MAX_MINTS().call(), 10 )
                const maxFreeMintsPerWallet = 99999; //parseInt( await _contract.methods.MAX_FREE_MINTS_PER_WALLET().call(), 10 )
                const maxFreeSupply = 99999; // parseInt( await _contract.methods.MAX_FREE_SUPPLY().call(), 10 )
                let mintPrice = parseInt( await _contract.methods.MINT_PRICE().call(), 10 ) / ( 10 ** 18 )
                const balance = parseInt( await _contract.methods.balanceOf( connectedWallet ).call(), 10 )
                const numFree = parseInt( await _contract.methods.NUM_FREE().call(), 10 )
                setMintingIsPaused( await _contract.methods.isPaused().call() )

                // If the minting process is there's a free mint supply
                // allotment, change some things to reflect it.
                if ( FREE_MINT_MODE === 'free_mint_num' ) {
                    mintPrice = totalSupply < maxFreeSupply ? 0 : mintPrice
                    maxMintsPerWallet = totalSupply < maxFreeSupply ? maxFreeMintsPerWallet : maxMintsPerWallet
                }

                setTotalSupply( totalSupply )
                setMaxSupply( maxSupply )
                setMaxMintAmount( maxMintsPerWallet )
                setNumFree( numFree )
                setBalance( balance )
                setPrice( mintPrice )

                // Change the mint amount currently inputted if it's too big.
                if ( mintAmount > maxMintsPerWallet - balance ) {
                    const inputAmount = maxMintsPerWallet - balance
                    setMintAmount( inputAmount < 0 ? 0 : inputAmount )
                }

            } catch (err) {
                setConnectionMessage( 
                    <div className="alert alert-error shadow-lg">
                        <div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            <span>Could not connect to the contract, please check if your MetaMask is using the Ethereum Network.</span>
                        </div>
                    </div>
                )
            }
        }
        update()
    }, [connectedWallet, contract, mintAmount] )

    // Once connected, start communicating with the contract.
    useEffect( () => {
        const init = async () => {
            window.web3 = new Web3( window.ethereum )
            const _contract = new window.web3.eth.Contract( ABI, CONTRACT )
            setContract( _contract )

            // This is so that you can access the contract from the browser if
            // you want to test things.
            window.contract = _contract 

            updateContractStatus( _contract )
        }
        if ( connectedWallet && ! contract ) {
            init()
        }
    }, [connectedWallet, updateContractStatus, contract] )

    // Adjust the price to be paid depending on the mint amount.
    useEffect( () => {
        if ( FREE_MINT_MODE === 'first_free' ) {
            let payForAmount = mintAmount
            if ( balance < numFree ) {
                payForAmount -= numFree - balance
            }
            if ( payForAmount < 0 ) {
                payForAmount = 0
            }
            setCurrentPriceToPay( payForAmount * price )
        } else {
            setCurrentPriceToPay( mintAmount * price === 0 ? 0 : mintAmount * price )
        }
    }, [totalSupply, mintAmount, numFree, price, balance])

    // Mint!
    const mint = () => {
        setErrorMessage( '' )
        setSuccessMessage( '' )
        setSuccessTransactionUrlMessage( '' )
        setIsBusy( true )

        const data = { 
            from: connectedWallet, 
            value: currentPriceToPay * 10 ** 18,
        }

        contract.methods
            .mint( mintAmount )
            .estimateGas( data )
            .then( () => {
                contract.methods
                    .mint( mintAmount )
                    .send( data )
                    .on('transactionHash', transactionHash => {
                        setErrorMessage( '' )
                        setSuccessMessage( 'Pending' )
                        setSuccessTransactionUrlMessage( getTransactionEtherscanLink( transactionHash ) )
                    } )
                    .then( result => {
                        if ( result.status ) {
                            setErrorMessage( '' )
                            setSuccessMessage( 'Mint successful!' )
                            setSuccessTransactionUrlMessage( getTransactionEtherscanLink( result.transactionHash ) )
                        }
                    })
                    .catch( err => {
                        setSuccessMessage( '' )
                        setErrorMessage( `Couldn't mint: ${ err.message.replace( /\{[\s\S]*$/, '' ) }` )
                    })
                    .finally( () => {
                        updateContractStatus()
                        setIsBusy( false )
                    } )
            })
            .catch( err => {
                setSuccessMessage( '' )
                setErrorMessage( `Couldn't mint: ${ err.message.replace( /\{[\s\S]*$/, '' ) }` )
                updateContractStatus()
                setIsBusy( false )
            })
    }

    // This updates the minted amount, only runs when minting is ongoing.
    const [ supplyInterval, setSupplyInterval ] = useState( null )
    useEffect( () => {
        if ( contract && ! mintingIsPaused && maxSupply && totalSupply < maxSupply ) {
            if ( ! supplyInterval ) {
                setSupplyInterval( true )
                const checker = async () => {
                    setTotalSupply( parseInt( await contract.methods.totalSupply().call(), 10 ) )
                    updateContractStatus()

                    if ( contract && ! mintingIsPaused && maxSupply && totalSupply < maxSupply ) {
                        setTimeout( checker, 1000 )
                    } else {
                        setSupplyInterval( false )
                    }
                }
                checker()
            }
        }
    }, [contract, maxSupply, mintingIsPaused, supplyInterval, totalSupply, updateContractStatus])

    // Something went wrong and we can't continue minting.
    if ( ! window.ethereum || ! window.ethereum.isMetaMask || ! isInitialized ) {
        return props.showUnconnectedError ? (
            <div className="alert alert-error shadow-lg">
                <div>
                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>You will need to have MetaMask to interact with this website.</span>
                </div>
            </div>
        ) : null
    }

    const primaryButtonClasses = classnames(
        'btn btn-lg btn-primary',
        { 'loading': isBusy },
    )

    return ( 
        <div className="nftarea-wrapper space-y-4 text-sm">
            { ! connectedWallet && <button onClick={ doConnect } className={ primaryButtonClasses }>{ isBusy ? 'Connecting...' : 'Connect Wallet' }</button> }
            { connectedWallet && contract && ! maxSupply && <div>{ connectionMessage }</div> }
            { connectedWallet && contract && maxSupply && mintingIsPaused && <button className="btn btn-lg btn-primary" disabled={ true }>Minting Soon</button> }

            { connectedWallet && contract && ! mintingIsPaused && maxSupply && <>
                { totalSupply < maxSupply && (
                    <div className="flex gap-4 justify-center">
                        <input 
                            className="input input-bordered input-primary"
                            type="number" 
                            onChange={ event => {
                                const newValue = event.target.value
                                if ( newValue === '' || ( newValue > 0 && newValue <= maxMintAmount - balance ) ) {
                                    setMintAmount( newValue )
                                }
                            }}
                            onBlur={ event => {
                                const newValue = event.target.value
                                if ( newValue === '' ) {
                                    setMintAmount( 1 )
                                } else if ( newValue < 0 ) {
                                    setMintAmount( 0 )
                                } else if ( newValue > maxMintAmount - balance ) {
                                    setMintAmount( maxMintAmount - balance )
                                }
                            }}
                            value={ mintAmount }
                        />
                        <button 
                            className="btn btn-square btn-outline flex-none"
                            disabled={ mintAmount <= 1 }
                            onClick={ event => {
                                const newValue = mintAmount - 1
                                if ( newValue > 0 ) {
                                    setMintAmount( newValue )
                                }
                                event.preventDefault()
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M400 288h-352c-17.69 0-32-14.32-32-32.01s14.31-31.99 32-31.99h352c17.69 0 32 14.3 32 31.99S417.7 288 400 288z"/></svg>
                        </button>
                        <button 
                            className="btn btn-square btn-outline flex-none"
                            disabled={ mintAmount >= maxMintAmount - balance }
                            onClick={ event => {
                                const newValue = mintAmount + 1
                                if ( newValue <= maxMintAmount - balance ) {
                                    setMintAmount( newValue )
                                }
                                event.preventDefault()
                            }}
                            >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M432 256c0 17.69-14.33 32.01-32 32.01H256v144c0 17.69-14.33 31.99-32 31.99s-32-14.3-32-31.99v-144H48c-17.67 0-32-14.32-32-32.01s14.33-31.99 32-31.99H192v-144c0-17.69 14.33-32.01 32-32.01s32 14.32 32 32.01v144h144C417.7 224 432 238.3 432 256z"/></svg>    
                        </button>
                    </div>
                ) }
                { totalSupply < maxSupply && <p>{ balance >= maxMintAmount && price ? `Price: ${ price } eth each` : ( currentPriceToPay ? `Total: ${ currentPriceToPay } eth` : 'Total: Free' ) }</p> }

                { totalSupply < maxSupply && balance < maxMintAmount && <button className={ primaryButtonClasses } onClick={ mint } disabled={ isBusy }>{ ! isBusy ? 'Go Mint' : 'Minting...' }</button> } 
                { totalSupply < maxSupply && balance >= maxMintAmount && <button className="btn btn-lg btn-secondary" disabled={ true }>Reached mint limit</button> } 
                { totalSupply >= maxSupply && <button className="btn btn-lg btn-secondary" disabled={ true }>Sold out</button> } 

                { errorMessage && <div className="alert alert-error shadow-lg">
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span>{ errorMessage }</span>
                    </div>
                </div> }
                { successMessage && <div className="alert alert-success shadow-lg">
                    <div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <span>{ successMessage }</span>
                    </div>
                    { successTransactionUrlMessage && <div className="flex-none">
                        <a className="btn btn-sm btn-ghost btn-secondary" href={ successTransactionUrlMessage } target="_etherscan">View Transaction</a>
                    </div> }
                </div> }

                { <div className="supply">{ totalSupply } / { maxSupply } Minted</div> }
            </> }
        </div>
    )
}

NftArea.defaultProps = {
    // If `true` if a warning will be showed when no MetaMask is found.
    showUnconnectedError: true,
}

export default NftArea